import React, { createContext, useContext, useState } from "react";
import { LikeModal } from "@/components/feedback/like-modal";
import { fetchApi } from "@/lib/api";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { DislikeModal } from "@/components/feedback/dislike-modal";

export type FeedbackType = "like" | "dislike";

export type FeedbackContextType = {
  content_type: string;
  content_id: number;
  feedback_type: FeedbackType;
  issue_type?: string;
  details?: string;
  context?: Record<string, unknown>;
};

type ModalType = "like" | "dislike"; // Extend as needed

type FeedbackModalState = {
  open: boolean;
  type?: ModalType;
  contentType?: FeedbackContextType;
  display?: React.ReactNode;
};

type FeedbackContextState = {
  showModal: (
    type: ModalType,
    contentType: FeedbackContextType,
    display?: React.ReactNode
  ) => void;
  hideModal: () => void;
  modal: FeedbackModalState;
  submitFeedback: (contentType: FeedbackContextType) => Promise<void>;
  isSubmitting: boolean;
  error: unknown;
};

const FeedbackContext = createContext<FeedbackContextState | undefined>(
  undefined
);

export function useFeedback() {
  const context = useContext(FeedbackContext);

  if (context === undefined) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }

  return context;
}

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modal, setModal] = useState<FeedbackModalState>({ open: false });

  const showModal = (
    type: ModalType,
    contentType: FeedbackContextType,
    display?: React.ReactNode
  ) => {
    setModal({ open: true, type, contentType, display });
  };

  const hideModal = () => setModal({ open: false });

  const submitFeedbackApi = async (contentType: FeedbackContextType) => {
    return await fetchApi("/feedback/", {
      method: "POST",
      body: JSON.stringify(contentType),
    });
  };

  const {
    mutateAsync: submitFeedback,
    isPending,
    error,
  } = useMutation({
    mutationFn: submitFeedbackApi,
    onSuccess: () => {
      toast.success("Feedback submitted", {
        description: "Feedback submitted successfully",
      });
      hideModal();
    },
    onError: (error) => {
      console.error(error);
      toast.error("Feedback submission failed", {
        description: "Feedback submission failed",
      });
    },
  });

  return (
    <FeedbackContext.Provider
      value={{
        showModal,
        hideModal,
        modal,
        submitFeedback,
        isSubmitting: isPending,
        error,
      }}
    >
      {children}
      <LikeModal />
      <DislikeModal />
    </FeedbackContext.Provider>
  );
};
